import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { importGoogleLibrary } from '../../services/googleMaps';
import { provinceCode } from '../../utils/territory';

const componentValue = (components, type) => components?.find((item) => item.types?.includes(type))?.longText || '';
const CUYO_BOUNDS = { west: -70.75, north: -28.20, east: -64.95, south: -36.75 };

export default function GoogleAddressAutocomplete({ value, onChange, onPlaceSelect, onSearchingChange }) {
  const hostRef = useRef(null);
  const elementRef = useRef(null);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const onPlaceSelectRef = useRef(onPlaceSelect);
  const onSearchingChangeRef = useRef(onSearchingChange);
  const [failed, setFailed] = useState(false);

  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => { onPlaceSelectRef.current = onPlaceSelect; }, [onPlaceSelect]);
  useEffect(() => { onSearchingChangeRef.current = onSearchingChange; }, [onSearchingChange]);
  useEffect(() => { valueRef.current = value; }, [value]);

  useEffect(() => {
    let active = true;
    importGoogleLibrary('places').then(({ PlaceAutocompleteElement }) => {
      if (!active || !hostRef.current) return;
      const autocomplete = new PlaceAutocompleteElement({
        includedRegionCodes: ['ar'],
        locationRestriction: CUYO_BOUNDS,
      });
      autocomplete.placeholder = 'Empezá a escribir la calle y el número';
      autocomplete.value = valueRef.current || '';
      autocomplete.addEventListener('gmp-select', async (event) => {
        onSearchingChangeRef.current?.(false);
        const place = event.placePrediction.toPlace();
        await place.fetchFields({ fields: ['id', 'formattedAddress', 'location', 'addressComponents'] });
        const lat = place.location?.lat();
        const lng = place.location?.lng();
        const components = place.addressComponents || [];
        const department = componentValue(components, 'administrative_area_level_2')
          || componentValue(components, 'locality');
        const city = componentValue(components, 'locality')
          || componentValue(components, 'administrative_area_level_2');
        const neighborhood = componentValue(components, 'sublocality_level_1')
          || componentValue(components, 'neighborhood');
        const province = provinceCode(componentValue(components, 'administrative_area_level_1'));
        onPlaceSelectRef.current?.({ address: place.formattedAddress || autocomplete.value, lat, lng, placeId:place.id, province, city, department, neighborhood });
      });
      autocomplete.addEventListener('input', () => {
        onChangeRef.current?.(autocomplete.value);
        onSearchingChangeRef.current?.(Boolean(autocomplete.value?.trim()));
      });
      hostRef.current.replaceChildren(autocomplete);
      elementRef.current = autocomplete;
    }).catch(() => active && setFailed(true));
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (elementRef.current && elementRef.current.value !== value) elementRef.current.value = value || '';
  }, [value]);

  if (failed) return <FallbackInput value={value} onChange={(event) => onChange?.(event.target.value)} placeholder="Calle y número" />;
  return <AutocompleteHost ref={hostRef} />;
}

const sharedInput = `
  width:100%;height:56px;padding:0 17px;font:inherit;font-size:16px;color:#29474f;background:#fff;border:1px solid rgba(41,71,79,.15);border-radius:16px;box-sizing:border-box;
`;
const AutocompleteHost = styled.div`
  min-height:56px;
  gmp-place-autocomplete{
    display:block;
    width:100%;
    min-height:56px;
    padding:0!important;
    border:0!important;
    border-radius:0!important;
    background:transparent!important;
    box-sizing:border-box;
    outline:none!important;
    box-shadow:none!important;
  }
  /* El widget expone su aro de foco como una Shadow Part. */
  gmp-place-autocomplete::part(focus-ring){
    background-color:transparent!important;
    border-color:#c66f4e!important;
    outline-color:#c66f4e!important;
    box-shadow:0 0 0 1px #c66f4e!important;
  }
`;
const FallbackInput = styled.input`${sharedInput}&:focus{outline:none;border-color:#c66f4e;box-shadow:0 0 0 3px rgba(198,111,78,.15);}`;
